import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, Observable, share } from 'rxjs';

/**
 * Os GETs em andamento, por endereço e escritório.
 *
 * Fica num serviço, não numa variável do arquivo: no servidor (SSR) cada
 * página tem o seu, e um visitante nunca recebe a resposta de outro.
 */
@Injectable({ providedIn: 'root' })
export class InflightRequests {
	readonly pending = new Map<string, Observable<HttpEvent<unknown>>>();
}

/**
 * GET igual a outro que ainda não voltou espera a mesma resposta, em vez de
 * ir de novo ao servidor.
 *
 * Várias partes da tela pedem a mesma coisa ao mesmo tempo sem saber uma da
 * outra (a casca e a página pedem a cobrança; a obra e a aba Arquivos pedem os
 * arquivos). Antes, cada uma virava uma chamada. Só junta o que está em
 * andamento: terminou, a próxima vai ao servidor, então nada fica velho. E uma
 * gravação no meio do caminho separa o antes do depois.
 *
 * Vem depois do `authInterceptor`, para a chave levar o escritório
 * (`x-company-id`) e o login.
 */
export const inflightInterceptor: HttpInterceptorFn = (req, next) => {
	const inflight = inject(InflightRequests).pending;

	/*
	 * Gravou algo (POST, PATCH, DELETE): um GET feito depois tem que ver o dado
	 * novo, e não pegar carona num GET que saiu antes da gravação.
	 */
	if (req.method !== 'GET') {
		inflight.clear();
		return next(req);
	}
	if (req.reportProgress || req.responseType !== 'json') return next(req);

	const key = [req.urlWithParams, req.headers.get('x-company-id') ?? '', req.headers.get('Authorization') ?? ''].join(
		'|'
	);

	const running = inflight.get(key);
	if (running) return running;

	const request: Observable<HttpEvent<unknown>> = next(req).pipe(
		/* Só apaga se ainda for ele: depois de uma gravação, outro GET igual pode já estar no lugar. */
		finalize(() => {
			if (inflight.get(key) === request) inflight.delete(key);
		}),
		share()
	);
	inflight.set(key, request);
	return request;
};
